<template>
  <div class="payment-link">
    <!-- Logo da campanha FORA da caixa (sobre o fundo neutro), para não apagar
         um logo colorido sobre um header colorido. -->
    <div v-if="campaignFile && !loading && !loadError" class="pl-brand">
      <FileImage :file="campaignFile" class="pl-brand-img" />
    </div>

    <div v-if="loading" class="pl-center">
      <div class="pl-spinner"></div>
      <p>Carregando…</p>
    </div>

    <div v-else-if="loadError" class="pl-center">
      <h2>Link indisponível</h2>
      <p>{{ loadError }}</p>
    </div>

    <!-- Pix Automático gerado: mostra o QR -->
    <div v-else-if="pixResult" class="pl-card">
      <header class="pl-header">
        <h1 class="pl-title">{{ pageTitle }}</h1>
      </header>
      <section class="pl-pix">
        <h3 class="pl-pix-title">Escaneie para pagar com Pix</h3>
        <img v-if="pixResult.qrCodeBase64" :src="pixQrSrc" alt="QR Code Pix" class="pl-qr" />
        <p class="pl-pix-label">Pix copia e cola:</p>
        <textarea readonly class="pl-copypaste" :value="pixResult.copyPaste"></textarea>
        <button class="pl-submit" type="button" @click="copyPix">Copiar código</button>
        <p v-if="donationId" class="pl-donation-id">Doação nº <strong>{{ donationId }}</strong></p>
        <p class="pl-secure">Assinatura mensal · a cobrança se repete automaticamente todo mês.</p>
      </section>
    </div>

    <div v-else-if="done" class="pl-center pl-done">
      <h2>Doação recebida! 💜</h2>
      <p>Obrigado por apoiar {{ campaignName }}.</p>
      <p v-if="donationId" class="pl-donation-id">Doação nº <strong>{{ donationId }}</strong></p>
    </div>

    <div v-else class="pl-card">
      <header class="pl-header">
        <h1 class="pl-title">{{ pageTitle }}</h1>
        <p v-if="config.customMessage" class="pl-message">{{ config.customMessage }}</p>
      </header>

      <!-- 1. O QUE ESTÁ SENDO PAGO — a doação já foi montada pelo parceiro; a
           pessoa confere o que está pagando antes de digitar qualquer dado. -->
      <section class="pl-summary">
        <div class="pl-summary-main">
          <div class="pl-summary-item">{{ summaryItemName }}</div>
          <div class="pl-summary-amount">
            <strong>{{ formatMoney(amountReais) }}</strong>
            <span v-if="isMonthly">por mês</span>
          </div>
          <div class="pl-tags">
            <span class="pl-tag">{{ isMonthly ? "Doação mensal" : "Doação única" }}</span>
            <span v-for="m in methods" :key="'tag-' + m.id" class="pl-tag">{{ m.label }}</span>
          </div>
          <div v-if="donationProduct && (productImage || donationProduct.description)" class="pl-product">
            <img v-if="productImage" :src="productImage" :alt="donationProduct.name" class="pl-product-img" />
            <p v-if="donationProduct.description" class="pl-product-desc" v-html="donationProduct.description"></p>
          </div>
        </div>
      </section>

      <!-- Seletor de meio de pagamento apenas quando o link permite mais de um. -->
      <section v-if="methods.length > 1" class="pl-methods">
        <label v-for="m in methods" :key="m.id" class="pl-method"
               :class="{ active: selectedMethod === m.id }">
          <input type="radio" :value="m.id" v-model="selectedMethod" />
          {{ m.label }}
        </label>
      </section>

      <form @submit.prevent="submit">
        <!-- 2. QUEM ESTÁ DOANDO — veio pré-preenchido do parceiro; mostra mascarado
             para conferência e um "alterar" discreto abre os campos se algo estiver errado. -->
        <section class="pl-donor">
          <div class="pl-donor-head">
            <h3>Quem está doando</h3>
            <button type="button" class="pl-link-btn" @click="donorEditing = !donorEditing">
              {{ donorEditing ? "pronto" : "alterar" }}
            </button>
          </div>

          <ul v-if="!donorEditing" class="pl-donor-read">
            <li>{{ maskName(donor.name) }}</li>
            <li>{{ maskEmail(donor.email) }}</li>
            <li>{{ maskCpf(donor.cpf) }}</li>
            <li v-if="donor.phone">{{ maskPhone(donor.phone) }}</li>
          </ul>

          <div v-else class="pl-donor-edit">
            <label>Nome completo
              <input v-model="donor.name" type="text" class="pl-input" autocomplete="name" required />
            </label>
            <label>E-mail
              <input v-model="donor.email" type="email" class="pl-input" autocomplete="email" required />
            </label>
            <div class="pl-row">
              <label>CPF
                <the-mask :mask="'###.###.###-##'" v-model="donor.cpf" type="tel" class="pl-input" required />
              </label>
              <label>Celular
                <the-mask :mask="'(##) #####-####'" v-model="donor.phone" type="tel" class="pl-input" required />
              </label>
            </div>
          </div>
        </section>

        <!-- 3. PAGAMENTO — campos do cartão só quando o meio é cartão. -->
        <section class="pl-form">
          <template v-if="selectedMethod === PaymentMethod.CreditCard">
            <h3>Cartão de crédito</h3>
            <label>Número do cartão
              <the-mask :mask="'#### #### #### ####'" v-model="card.number" type="tel" class="pl-input"
                        autocomplete="cc-number" placeholder="0000 0000 0000 0000" required />
            </label>
            <label>Nome impresso no cartão
              <input v-model="card.holderName" type="text" class="pl-input pl-uppercase" autocomplete="cc-name"
                     required @input="card.holderName = card.holderName.toUpperCase()" />
            </label>
            <div class="pl-row">
              <label>Validade
                <the-mask :mask="'##/##'" v-model="card.expiration" type="tel" class="pl-input"
                          autocomplete="cc-exp" placeholder="MM/AA" required />
              </label>
              <label>CVV
                <the-mask :mask="'####'" v-model="card.cvv" type="tel" class="pl-input"
                          autocomplete="cc-csc" placeholder="123" required />
              </label>
            </div>
            <label v-if="installmentOptions.length > 1">Parcelas
              <select v-model.number="selectedInstallments" class="pl-input">
                <option v-for="opt in installmentOptions" :key="opt.installment" :value="opt.installment">
                  {{ opt.installment }}x de {{ formatMoney(opt.value) }}<template v-if="opt.installment > 1"> (total {{ formatMoney(installmentTotal(opt)) }})</template>
                </option>
              </select>
            </label>

            <div v-if="hasFee" class="pl-breakdown">
              <div class="pl-breakdown-row"><span>Doação</span><span>{{ formatMoney(amountReais) }}</span></div>
              <div class="pl-breakdown-row"><span>Taxa do meio de pagamento</span><span>{{ formatMoney(feeReais) }}</span></div>
              <div class="pl-breakdown-row pl-breakdown-total"><span>Total a pagar</span><span>{{ formatMoney(totalReais) }}</span></div>
            </div>
          </template>

          <p v-if="submitError" class="pl-error">{{ submitError }}</p>

          <button class="pl-submit" type="submit" :disabled="submitting">
            {{ submitLabel }}
          </button>
          <p class="pl-secure">🔒 Pagamento processado com segurança pela Abrace uma Causa.</p>
        </section>
      </form>
    </div>
  </div>
</template>

<script>
/* global grecaptcha */
import PaymentLinkService from "@/scripts/services/paymentLink.service.js";
import CampaignService from "@/scripts/services/campaign.service.js";
import PersonService from "@/scripts/services/person.service.js";
import CheckoutService from "@/scripts/services/checkout.service.js";
import PagarMeService from "@/scripts/services/pagarMe.service.js";
import PaymentInstallmentService from "@/scripts/services/paymentInstallment.service.js";
import FileImage from "@/components/FileImage.vue";
import Person from "@/scripts/models/person.model";
import PaymentMethod from "@/scripts/models/enums/paymentMethod.enum.js";
import PaymentMethodProvider from "@/scripts/models/enums/paymentMethodProvider.enum.js";
import DonationRecurrencePeriod from "@/scripts/models/enums/donationRecurrencePeriod.enum.js";

export default {
  name: "PaymentLink",
  components: { FileImage },
  data() {
    return {
      code: this.$route.params.code,
      loading: true,
      loadError: null,
      submitting: false,
      submitError: null,
      done: false,
      pixResult: null,
      donationId: null,
      donorEditing: false,
      PaymentMethod: PaymentMethod,
      config: {},
      item: {},
      campaignId: null,
      campaignConfig: null,
      selectedMethod: PaymentMethod.CreditCard,
      installmentOptions: [],
      selectedInstallments: 1,
      donor: { name: "", email: "", cpf: "", phone: "" },
      card: { number: "", holderName: "", expiration: "", cvv: "" },
      paymentLinkService: new PaymentLinkService(),
      campaignService: new CampaignService(),
      personService: new PersonService(),
      checkoutService: new CheckoutService(),
      pagarMeService: new PagarMeService(),
      paymentInstallmentService: new PaymentInstallmentService(),
    };
  },
  computed: {
    campaignName() {
      const c = this.campaignConfig && this.campaignConfig.campaign;
      return (c && c.company && c.company.entity && c.company.entity.name) || this.item.campaign || "";
    },
    pageTitle() {
      return this.config.customTitle || this.campaignName;
    },
    campaignFile() {
      const c = this.campaignConfig && this.campaignConfig.campaign;
      return (c && c.file) || null;
    },
    amountReais() {
      return (Number(this.item.amount) || 0) / 100;
    },
    recurrenceId() {
      const rec = (this.config.allowedRecurrences || []).map((r) => String(r).toLowerCase());
      return rec.includes("monthly") ? DonationRecurrencePeriod.EveryOneMonth : DonationRecurrencePeriod.Single;
    },
    isMonthly() {
      return this.recurrenceId !== DonationRecurrencePeriod.Single;
    },
    recurrenceLabel() {
      return this.recurrenceId === DonationRecurrencePeriod.Single ? "Única" : "Mensal (todo mês)";
    },
    summaryItemName() {
      return (this.donationProduct && this.donationProduct.name) || this.config.customTitle || "Doação";
    },
    methods() {
      const allowed = (this.config.allowedMethods || []).map((m) => String(m).toLowerCase());
      const list = [];
      if (allowed.includes("credit_card")) list.push({ id: PaymentMethod.CreditCard, label: "Cartão de crédito" });
      if (allowed.includes("pix_automatic")) list.push({ id: PaymentMethod.PixAutomatic, label: "Pix Automático (mensal)" });
      return list;
    },
    creditCardProvider() {
      const c = this.campaignConfig && this.campaignConfig.campaign;
      return c && c.campaignPaymentMethodProvider ? c.campaignPaymentMethodProvider.creditCardPaymentMethodProviderId : null;
    },
    donationProduct() {
      if (!this.campaignConfig || !this.item.donationProductId) return null;
      const projects = this.campaignConfig.projectList || [];
      const project = projects.find((p) => p.id === this.item.projectId);
      const list = (project && project.donationProductList) || [];
      return list.find((dp) => dp.id === this.item.donationProductId) || null;
    },
    productImage() {
      return this.donationProduct && this.donationProduct.file ? this.donationProduct.file.path : null;
    },
    pixQrSrc() {
      if (!this.pixResult || !this.pixResult.qrCodeBase64) return null;
      const v = this.pixResult.qrCodeBase64;
      return v.startsWith("data:") || v.startsWith("http") ? v : `data:image/png;base64,${v}`;
    },
    selectedInstallmentOption() {
      if (!this.installmentOptions.length) return null;
      return this.installmentOptions.find((o) => o.installment === this.selectedInstallments) || this.installmentOptions[0];
    },
    // Total efetivamente cobrado no cartão (doação + taxa do meio de pagamento, quando a taxa é
    // do doador). Sem taxa do doador, o total = valor da doação.
    totalReais() {
      const opt = this.selectedInstallmentOption;
      const total = opt ? this.installmentTotal(opt) : null;
      return total != null ? Number(total) : this.amountReais;
    },
    feeReais() {
      return Math.max(0, Math.round((this.totalReais - this.amountReais) * 100) / 100);
    },
    hasFee() {
      return this.selectedMethod === PaymentMethod.CreditCard && this.feeReais >= 0.01;
    },
    submitLabel() {
      if (this.submitting) return "Processando…";
      if (this.selectedMethod === PaymentMethod.PixAutomatic) return "Gerar Pix mensal";
      if (this.hasFee) return "Doar · pagar " + this.formatMoney(this.totalReais);
      return "Doar " + this.formatMoney(this.amountReais) + (this.isMonthly ? "/mês" : "");
    },
  },
  async created() {
    try {
      const link = await this.paymentLinkService.getLink(this.code);
      this.config = link.config || {};
      this.item = (link.items && link.items[0]) || {};
      this.campaignId = link.campaign_id;

      const prefill = link.donor_prefill || {};
      this.donor.name = prefill.name || "";
      this.donor.email = prefill.email || "";
      this.donor.cpf = (prefill.document || prefill.cpf || "").replace(/[^\d]/g, "");
      this.donor.phone = this.normalizePhone(prefill.phone || "");

      // meio de pagamento inicial: 1º permitido (preferindo cartão)
      if (this.methods.length) this.selectedMethod = this.methods[0].id;

      // Branding é BEST-EFFORT: as chamadas de identidade visual (Campaign/*) podem falhar
      // em produção (ex.: CORS). Uma falha de branding NÃO pode derrubar a página — os dados
      // do link (200 no GET público) já bastam para renderizar o formulário e doar. Se o
      // branding não carregar, segue com o tema padrão em vez de mostrar "Link indisponível".
      let cfg = null;
      try {
        cfg = await this.campaignService.getCampaignLandingPageConfigBySubdomain(false);
      } catch (e) {
        cfg = null;
      }
      if (!cfg && this.campaignId) {
        try {
          cfg = await this.campaignService.getCampaignLandingPageConfigByCampaignId(this.campaignId);
        } catch (e) {
          cfg = null;
        }
      }
      if (cfg) {
        this.campaignConfig = cfg;
        this.campaignId = cfg.campaignId || cfg.id || this.campaignId;
        this.applyTheme(cfg);
      }

      // Opções de parcelamento (cartão) — best-effort, não derruba a página se falhar.
      await this.loadInstallments();
    } catch (e) {
      const status = e && e.response && e.response.status;
      const apiMsg = e && e.response && e.response.data && e.response.data.error && e.response.data.error.message;
      // 410 Gone = link já utilizado, inativo ou expirado (ResolveLink no back).
      if (status === 410) {
        this.loadError = "Este link de pagamento não está mais disponível — ele já foi utilizado ou expirou.";
      } else {
        this.loadError = apiMsg || (e && e.message) || "Não foi possível carregar o link.";
      }
    } finally {
      this.loading = false;
    }
  },
  mounted() {
    const key = process.env.VUE_APP_GOOGLE_KEY_RECAPTCHA_V3;
    if (key && !document.getElementById("pl-recaptcha")) {
      const s = document.createElement("script");
      s.id = "pl-recaptcha";
      s.src = `https://www.google.com/recaptcha/enterprise.js?render=${key}`;
      document.head.appendChild(s);
    }
  },
  methods: {
    normalizePhone(raw) {
      let d = (raw || "").replace(/[^\d]/g, "");
      if (d.length > 11 && d.startsWith("55")) d = d.substring(2); // remove DDI
      return d;
    },
    // Máscaras só de EXIBIÇÃO (conferência): a pessoa reconhece o próprio dado sem
    // ele ficar legível para quem olha a tela por cima do ombro.
    maskName(v) {
      const partes = String(v || "").trim().split(/\s+/).filter(Boolean);
      if (!partes.length) return "—";
      const resto = partes.slice(1).map((p) => p[0] + "•".repeat(Math.max(p.length - 1, 1)));
      return [partes[0], ...resto].join(" ");
    },
    maskEmail(v) {
      const [u, d] = String(v || "").split("@");
      if (!u || !d) return "—";
      return u.slice(0, 2) + "•".repeat(Math.max(u.length - 2, 1)) + "@" + d;
    },
    maskCpf(v) {
      const n = String(v || "").replace(/\D/g, "");
      if (n.length !== 11) return n ? "•••" : "—";
      return `•••.${n.slice(3, 6)}.${n.slice(6, 9)}-••`;
    },
    maskPhone(v) {
      const n = String(v || "").replace(/\D/g, "");
      if (n.length < 10) return n ? "•••" : "—";
      return `(${n.slice(0, 2)}) ••••-${n.slice(-4)}`;
    },
    applyTheme(cfg) {
      if (!cfg) return;
      const map = {
        "--theme-primaria": "themePrimaria",
        "--theme-secundaria": "themeSecundaria",
        "--theme-destaque": "themeDestaque",
        "--theme-fundo": "themeFundo",
        "--theme-padrao": "themePadrao",
        "--theme-texto": "themeTexto",
      };
      const root = document.documentElement.style;
      Object.keys(map).forEach((cssVar) => {
        const raw = cfg[map[cssVar]];
        if (!raw) return;
        try {
          const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
          root.setProperty(cssVar, Array.isArray(arr) ? arr.join(", ") : String(raw));
        } catch (e) { /* ignora */ }
      });
    },
    formatMoney(v) {
      return (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    copyPix() {
      try {
        const ta = document.querySelector(".pl-copypaste");
        ta.select();
        document.execCommand("copy");
      } catch (e) { /* ignora */ }
    },
    async resolveDonor() {
      // Usa o model Person (que já inicializa user/address não-nulos), espelhando o
      // checkout simplificado do site. O back-end (PersonBusiness.ValidatePossibilityToAddPerson)
      // acessa person.User.ForceAucCompany — enviar sem o sub-objeto user causa NRE/500.
      const person = new Person();
      person.name = this.donor.name;
      person.email = this.donor.email;
      person.cpf = (this.donor.cpf || "").replace(/[^\d]/g, "");
      person.phone = (this.donor.phone || "").replace(/[^\d]/g, "");
      person.isEmployee = false;
      person.isEmployeeWithCorporativeEmail = false;
      const data = await this.personService.getOrCreateBySimplifiedDonation(person);
      return data && data.user ? data.user.id : null;
    },
    async tokenizeCard() {
      // Só Pagar.me tokeniza no front (API v5). Asaas: backend tokeniza a partir dos campos crus
      // + holder (nome/cpf) enviados no checkoutRequest.creditCardInfo.
      if (this.creditCardProvider === PaymentMethodProvider.PagarMe) {
        const resp = await this.pagarMeService.tokenizerCard({
          type: "card",
          card: {
            number: this.card.number.replace(/\s/g, ""),
            holder_name: this.card.holderName,
            exp_month: this.card.expiration.substring(0, 2),
            exp_year: this.card.expiration.substring(2, 4),
            cvv: this.card.cvv,
          },
        });
        return resp.data.id;
      }
      return null;
    },
    async recaptchaToken() {
      const key = process.env.VUE_APP_GOOGLE_KEY_RECAPTCHA_V3;
      if (!key || typeof grecaptcha === "undefined" || !grecaptcha.enterprise) return null;
      return grecaptcha.enterprise.execute(key, { action: "submit" });
    },
    installmentTotal(opt) {
      // O endpoint de parcelas serializa TotalValue como total_value (snake_case); fallback camelCase.
      return opt && (opt.total_value != null ? opt.total_value : opt.totalValue);
    },
    async loadInstallments() {
      this.installmentOptions = [];
      this.selectedInstallments = 1;
      // Parcelamento só se o link permite cartão e já temos campanha + valor.
      const allowsCard = this.methods.some((m) => m.id === PaymentMethod.CreditCard);
      if (!allowsCard || !this.campaignId || !this.amountReais) return;
      try {
        const list = await this.paymentInstallmentService.getFirstInstallments(
          this.campaignId,
          this.amountReais,
          PaymentMethod.CreditCard,
          true
        );
        let options = Array.isArray(list) ? list : [];
        // Teto opcional por link (config.maxInstallments); senão herda o máximo da campanha.
        const cap = this.config && this.config.maxInstallments;
        if (cap && cap > 0) options = options.filter((o) => o.installment <= cap);
        this.installmentOptions = options;
      } catch (e) {
        this.installmentOptions = []; // best-effort: sem parcelas, segue 1x
      }
    },
    baseOrder(userId, paymentMethodId, tokenRecaptcha) {
      return {
        userId: userId,
        cartId: 0,
        campaignId: this.campaignId,
        paymentMethodId: paymentMethodId,
        paymentRecurrencePeriodId: this.recurrenceId,
        installments: paymentMethodId === PaymentMethod.CreditCard ? this.selectedInstallments : 1,
        totalValue: this.amountReais,
        // Raiz e item tem tipos diferentes no back: CheckoutViewModel.DonationProductId e int
        // (null derruba o model binding com 400), CartItemCheckoutViewModel.DonationProductId e int?.
        donationProductId: this.item.donationProductId || 0,
        coin: "BRL",
        linkId: this.code,
        cartItems: [
          {
            id: 0,
            projectId: this.item.projectId,
            itemValue: this.amountReais,
            donationProductId: this.item.donationProductId || null,
            project: { id: this.item.projectId },
          },
        ],
        expectedCarts: [],
        tokenRecaptcha: tokenRecaptcha,
      };
    },
    async submit() {
      this.submitError = null;
      this.submitting = true;
      try {
        const userId = await this.resolveDonor();
        if (!userId) throw new Error("Não foi possível identificar o doador.");
        const tokenRecaptcha = await this.recaptchaToken();

        if (this.selectedMethod === PaymentMethod.PixAutomatic) {
          const order = this.baseOrder(userId, PaymentMethod.PixAutomatic, tokenRecaptcha);
          order.pix = { expires_in: 86400 };
          const response = await this.checkoutService.postCheckout(order);
          this.donationId = (response && response.cart_id) || null; // id da doação (cart)
          this.pixResult = this.extractPix(response);
          if (!this.pixResult) this.done = true; // sem QR na resposta: ao menos confirma
          return;
        }

        // Cartão de crédito
        const cardToken = await this.tokenizeCard();
        const order = this.baseOrder(userId, PaymentMethod.CreditCard, tokenRecaptcha);
        const expMonth = this.card.expiration.substring(0, 2);
        const expYear = "20" + this.card.expiration.substring(2, 4);
        order.creditCard = {
          cardToken: cardToken,
          saveCard: false,
          cardNumber: this.card.number.replace(/\s/g, ""),
          expirationMonth: expMonth,
          expirationYear: expYear,
          holderName: this.card.holderName,
          cvv: this.card.cvv,
        };
        // Asaas usa os dados crus + holder (nome/cpf) daqui; PagarMe usa o token.
        order.checkoutRequest = {
          creditCardInfo: {
            cardNumber: this.card.number.replace(/\s/g, ""),
            cvv: this.card.cvv,
            expirationMonth: expMonth,
            expirationYear: expYear,
            holder: { name: this.card.holderName, cpf: (this.donor.cpf || "").replace(/[^\d]/g, "") },
          },
        };

        const response = await this.checkoutService.postCheckout(order);
        if (response) {
          this.donationId = (response && response.cart_id) || null; // id da doação (cart)
          this.done = true;
        }
      } catch (e) {
        this.submitError =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          "Não foi possível processar a doação. Verifique os dados e tente novamente.";
      } finally {
        this.submitting = false;
      }
    },
    extractPix(response) {
      const charge = (response && response.charges && response.charges[0]) || {};
      // O back serializa o charge do Pix com estes campos (CheckoutOrderPayment):
      //   qr_code                 -> Asaas/Itaú: IMAGEM base64 (base_64=true) | PagarMe: EMV texto
      //   qr_code_url             -> PagarMe: URL da imagem do QR
      //   qr_code_copy_and_paste  -> copia e cola (payload EMV)
      //   base_64                 -> true quando qr_code é imagem base64
      const isBase64 = charge.base_64 === true || charge.base_64 === "true";
      // Copia e cola: campo dedicado; no PagarMe o qr_code também é o EMV.
      const copy = charge.qr_code_copy_and_paste || (!isBase64 ? charge.qr_code : null) || null;
      // Imagem do QR: base64 (Asaas/Itaú) vem em qr_code; senão a URL do PagarMe.
      let img = null;
      if (isBase64 && charge.qr_code) img = charge.qr_code;
      else if (charge.qr_code_url) img = charge.qr_code_url;
      if (!copy && !img) return null;
      return { qrCodeBase64: img, copyPaste: copy };
    },
  },
};
</script>

<style scoped>
/* O tema real vem do branding da campanha (applyTheme injeta --theme-* no :root).
   Aqui derivamos os tokens da tela desses valores, com um neutro de fallback — de
   propósito, para a página funcionar mesmo sem branding em vez de não abrir. */
.payment-link {
  --pl-accent: rgb(var(--theme-primaria, 107, 70, 193));
  --pl-accent-ink: #ffffff;
  --pl-ink: rgb(var(--theme-texto, 26, 26, 46));
  --pl-ink-soft: #5b6178;
  --pl-line: #e3e5ec;
  --pl-surface: #ffffff;
  --pl-ground: rgb(var(--theme-fundo, 244, 245, 249));
  --pl-ok: #0f7b5f;
  --pl-err: #b42318;

  min-height: 100vh;
  background: var(--pl-ground);
  color: var(--pl-ink);
  padding: 24px 16px;
  display: flex; flex-direction: column; justify-content: flex-start; align-items: center;
  font-family: var(--font-principal, system-ui, "Segoe UI", Roboto), sans-serif;
}
.payment-link * { box-sizing: border-box; }

.pl-brand { text-align: center; margin: 8px 0 16px; }
.pl-brand-img { max-height: 56px; max-width: 70%; display: inline-block; }

.pl-card {
  width: 100%; max-width: 460px;
  background: var(--pl-surface);
  border: 1px solid var(--pl-line);
  border-radius: 16px;
  overflow: hidden;
}
.pl-header { padding: 20px 20px 0; }
.pl-title { margin: 0; font-size: 1.25rem; line-height: 1.25; color: var(--pl-ink); }
.pl-message { margin: 6px 0 0; color: var(--pl-ink-soft); font-size: .9rem; }

/* 1. Resumo — o que está sendo pago */
.pl-summary { padding: 16px 20px; }
.pl-summary-main {
  border: 1px solid var(--pl-line);
  border-radius: 12px;
  padding: 14px 16px;
  background: var(--pl-ground);
}
.pl-summary-item { font-weight: 700; color: var(--pl-ink); }
.pl-summary-amount { display: flex; align-items: baseline; gap: 6px; margin-top: 6px; }
.pl-summary-amount strong { font-size: 1.9rem; letter-spacing: -.02em; font-variant-numeric: tabular-nums; }
.pl-summary-amount span { color: var(--pl-ink-soft); font-size: .95rem; }
.pl-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.pl-tag {
  font-size: .75rem; font-weight: 600; padding: 3px 9px; border-radius: 999px;
  background: var(--pl-surface); border: 1px solid var(--pl-line); color: var(--pl-ink-soft);
}
.pl-product { display: flex; gap: 10px; align-items: center; margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--pl-line); }
.pl-product-img { width: 48px; height: 48px; object-fit: cover; border-radius: 8px; flex: 0 0 auto; }
.pl-product-desc { margin: 0; font-size: .8rem; color: var(--pl-ink-soft); }

/* Seletor de meio de pagamento */
.pl-methods { display: flex; gap: 8px; padding: 0 20px 4px; }
.pl-method {
  flex: 1; border: 1px solid var(--pl-line); border-radius: 10px; padding: 10px;
  font-size: .82rem; cursor: pointer; text-align: center; color: var(--pl-ink);
}
.pl-method.active { border-color: var(--pl-accent); box-shadow: 0 0 0 1px var(--pl-accent); }
.pl-method input { margin-right: 6px; }

/* 2. Doador (conferência) */
.pl-donor { padding: 8px 20px 4px; }
.pl-donor-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.pl-donor h3, .pl-form h3 {
  margin: 0 0 8px; font-size: .78rem; font-weight: 700; letter-spacing: .06em;
  text-transform: uppercase; color: var(--pl-ink-soft);
}
.pl-donor-read { margin: 0; padding: 0; font-size: .9rem; color: var(--pl-ink); }
.pl-donor-read li { list-style: none; padding: 2px 0; }
.pl-link-btn {
  background: none; border: 0; padding: 0; cursor: pointer; font: inherit;
  font-size: .82rem; font-weight: 600; color: var(--pl-accent); text-decoration: underline;
}

/* 3. Pagamento */
.pl-form, .pl-donor-edit { padding: 0; }
.pl-form { padding: 8px 20px 20px; }
.pl-form label, .pl-donor-edit label { display: block; font-size: .82rem; color: var(--pl-ink-soft); margin-top: 12px; }
.pl-input {
  width: 100%; margin-top: 4px; padding: 11px 12px;
  border: 1px solid var(--pl-line); border-radius: 10px;
  font: inherit; font-size: .95rem; color: var(--pl-ink); background: var(--pl-surface);
}
.pl-input:focus-visible { outline: 2px solid var(--pl-accent); outline-offset: 1px; }
.pl-uppercase { text-transform: uppercase; }
.pl-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.pl-submit {
  width: 100%; margin-top: 20px; padding: 14px;
  background: var(--pl-accent); color: var(--pl-accent-ink);
  border: 0; border-radius: 12px; cursor: pointer; font: inherit; font-weight: 700; font-size: 1rem;
}
.pl-submit:disabled { opacity: .6; cursor: default; }
.pl-secure { margin: 10px 0 0; text-align: center; font-size: .75rem; color: var(--pl-ink-soft); }
.pl-breakdown { margin-top: 14px; padding: 10px 12px; background: var(--pl-ground); border-radius: 10px; font-size: .9rem; color: var(--pl-ink); }
.pl-breakdown-row { display: flex; justify-content: space-between; padding: 3px 0; font-variant-numeric: tabular-nums; }
.pl-breakdown-row > span:first-child { color: var(--pl-ink-soft); }
.pl-breakdown-total { margin-top: 4px; padding-top: 8px; border-top: 1px solid var(--pl-line); font-weight: 700; }
.pl-breakdown-total > span:first-child { color: var(--pl-ink); }
.pl-error {
  margin: 12px 0 0; padding: 10px 12px; border-radius: 10px;
  background: #fdf1f0; border: 1px solid #f5c6c2; color: var(--pl-err); font-size: .85rem;
}

/* Estados centrais */
.pl-center { text-align: center; padding: 48px 20px; color: var(--pl-ink-soft); }
.pl-center h2 { margin: 0 0 8px; font-size: 1.15rem; color: var(--pl-ink); }
.pl-center p { margin: 0; }
.pl-done { min-height: 60vh; display: flex; flex-direction: column; justify-content: center; align-items: center; }
.pl-done h2 { color: var(--pl-ok); }

/* Pix (QR) */
.pl-pix { padding: 20px; text-align: center; }
.pl-pix-title { margin: 0 0 4px; font-size: 1rem; color: var(--pl-ink); }
.pl-qr { width: 240px; height: 240px; max-width: 100%; margin: 12px auto; display: block; }
.pl-pix-label { font-size: .82rem; color: var(--pl-ink-soft); margin-top: 12px; }
.pl-copypaste {
  width: 100%; height: 64px; font-size: .75rem; padding: 8px;
  border: 1px solid var(--pl-line); border-radius: 10px; resize: none; color: var(--pl-ink);
}
.pl-donation-id { font-size: .9rem; color: var(--pl-ink-soft); margin-top: 12px; }
.pl-donation-id strong { color: var(--pl-accent); }

.pl-spinner {
  width: 26px; height: 26px; margin: 0 auto 12px;
  border: 3px solid var(--pl-line); border-top-color: var(--pl-accent);
  border-radius: 50%; animation: pl-spin .8s linear infinite;
}
@keyframes pl-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .pl-spinner { animation-duration: 3s; } }
</style>
